import { AVAILABLE_FONTS } from '@/constant/fonts'
import { FontContext, type FontContextType, type FontOption } from '@/contexts/font-contexts'
import React, { useEffect, useState } from 'react'

interface FontProviderProps {
  children: React.ReactNode
}

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null

  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null
  return null
}

const setCookie = (name: string, value: string, days: number = 365): void => {
  if (typeof document === 'undefined') return

  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`
}

export const FontProvider: React.FC<FontProviderProps> = ({ children }) => {
  // Find 'Source Sans Pro font as default
  const defaultFont = AVAILABLE_FONTS.find((font) => font.name === 'Source Sans Pro') || AVAILABLE_FONTS[0]

  // Initialize state with cookie value or default to 'Source Sans Pro
  const getInitialFont = (): FontOption => {
    if (typeof document !== 'undefined') {
      const savedFont = getCookie('preferred-font')
      if (savedFont) {
        const font = AVAILABLE_FONTS.find((f) => f.value === savedFont)
        if (font) return font
      }
    }
    return defaultFont
  }

  const [currentFont, setCurrentFont] = useState<FontOption>(getInitialFont)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Apply font to CSS custom property immediately
    document.documentElement.style.setProperty('--font-sans', currentFont.value)

    // Update body class for potential additional styling
    document.body.className = document.body.className.replace(/font-\w+/g, '')
    document.body.classList.add(`font-${currentFont.name.toLowerCase().replace(/\s+/g, '-')}`)

    // Save to cookies (expires in 1 year) - but only if it's different from default
    if (currentFont.value !== defaultFont.value) setCookie('preferred-font', currentFont.value, 365)

    setIsLoading(false)
  }, [currentFont, defaultFont.value])

  const setFont = (font: FontOption) => {
    setCurrentFont(font)
    // Save to cookies immediately when font changes
    setCookie('preferred-font', font.value, 365)
  }

  const value: FontContextType = {
    currentFont,
    setFont,
    availableFonts: AVAILABLE_FONTS,
    isLoading,
  }

  return <FontContext.Provider value={value}>{children}</FontContext.Provider>
}
